import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  TableChart,
  DataObject,
  Undo,
  Redo,
  Save as SaveIcon,
  AutoAwesome as AIIcon
} from '@mui/icons-material';

interface Chapter {
  id: number;
  title: string;
  content?: string;
  level: number;
  status: string;
}

interface ReportEditorProps {
  chapter: Chapter | null;
  onSave: (content: string) => Promise<boolean>;
  saving: boolean;
  onGenerate?: () => void;
  generating?: boolean;
}

const ReportEditor: React.FC<ReportEditorProps> = ({
  chapter,
  onSave,
  saving,
  onGenerate,
  generating = false
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [content, setHasChanges] = useState(false);
  const [wordCount, setWordCount] = useState(0);

  // 当章节变化时，重置内容
  useEffect(() => {
    if (chapter && editorRef.current) {
      editorRef.current.innerHTML = chapter.content || '';
      setHasChanges(false);
      updateWordCount();
    }
  }, [chapter?.id, chapter?.content]);

  // 更新字数统计
  const updateWordCount = () => {
    if (editorRef.current) {
      const text = editorRef.current.innerText || '';
      const count = text.replace(/\s/g, '').length;
      setWordCount(count);
    }
  };

  // 处理内容变化
  const handleInput = () => {
    setHasChanges(true);
    updateWordCount();
  };

  // 执行格式化命令
  const execCommand = (command: string, value: string | undefined = undefined) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  // 格式化块（标题等）
  const formatBlock = (tag: string) => {
    document.execCommand('formatBlock', false, `<${tag}>`);
    editorRef.current?.focus();
  };

  // 插入表格
  const insertTable = () => {
    const tableHtml = `
      <table style="border-collapse: collapse; width: 100%; margin: 10px 0;">
        <tr>
          <th style="border: 1px solid #ddd; padding: 8px; background: #f5f5f5; text-align: left;">列1</th>
          <th style="border: 1px solid #ddd; padding: 8px; background: #f5f5f5; text-align: left;">列2</th>
          <th style="border: 1px solid #ddd; padding: 8px; background: #f5f5f5; text-align: left;">列3</th>
        </tr>
        <tr>
          <td style="border: 1px solid #ddd; padding: 8px;">数据1</td>
          <td style="border: 1px solid #ddd; padding: 8px;">数据2</td>
          <td style="border: 1px solid #ddd; padding: 8px;">数据3</td>
        </tr>
      </table>
    `;
    document.execCommand('insertHTML', false, tableHtml);
  };

  // 保存内容
  const handleSave = async () => {
    if (!editorRef.current || !chapter) return;

    const htmlContent = editorRef.current.innerHTML;
    const success = await onSave(htmlContent);
    if (success) {
      setHasChanges(false);
    }
  };

  // 监听保存事件
  useEffect(() => {
    const handleSaveEvent = () => {
      handleSave();
    };
    window.addEventListener('saveChapter', handleSaveEvent);
    return () => {
      window.removeEventListener('saveChapter', handleSaveEvent);
    };
  }, [chapter, onSave]);

  // 快捷键
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [chapter, onSave]);

  if (!chapter) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          color: 'text.secondary'
        }}
      >
        <Typography>请在左侧选择一个章节进行编辑</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden'
      }}
    >
      {/* 格式化工具栏 - 固定在顶部 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          p: 1,
          bgcolor: '#fafafa',
          borderBottom: 1,
          borderColor: 'divider',
          flexWrap: 'wrap'
        }}
      >
        {/* 段落格式 */}
        <Box sx={{ display: 'flex', alignItems: 'center', pr: 1, borderRight: 1, borderColor: 'divider', mr: 1 }}>
          <Select
            size="small"
            defaultValue="p"
            onChange={(e) => formatBlock(e.target.value)}
            sx={{
              height: 28,
              fontSize: '0.75rem',
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '&:hover .MuiOutlinedInput-notchedOutline': { border: '1px solid #e0e0e0' },
            }}
          >
            <MenuItem value="p" sx={{ fontSize: '0.75rem' }}>正文</MenuItem>
            <MenuItem value="h1" sx={{ fontSize: '0.75rem' }}>标题1</MenuItem>
            <MenuItem value="h2" sx={{ fontSize: '0.75rem' }}>标题2</MenuItem>
            <MenuItem value="h3" sx={{ fontSize: '0.75rem' }}>标题3</MenuItem>
          </Select>
        </Box>

        {/* 文字格式 */}
        <Box sx={{ display: 'flex', alignItems: 'center', pr: 1, borderRight: 1, borderColor: 'divider', mr: 1 }}>
          <Tooltip title="加粗 (Ctrl+B)">
            <IconButton size="small" onClick={() => execCommand('bold')} sx={{ p: 0.5 }}>
              <FormatBold fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="斜体 (Ctrl+I)">
            <IconButton size="small" onClick={() => execCommand('italic')} sx={{ p: 0.5 }}>
              <FormatItalic fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="下划线 (Ctrl+U)">
            <IconButton size="small" onClick={() => execCommand('underline')} sx={{ p: 0.5 }}>
              <FormatUnderlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* 列表 */}
        <Box sx={{ display: 'flex', alignItems: 'center', pr: 1, borderRight: 1, borderColor: 'divider', mr: 1 }}>
          <Tooltip title="无序列表">
            <IconButton size="small" onClick={() => execCommand('insertUnorderedList')} sx={{ p: 0.5 }}>
              <FormatListBulleted fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="有序列表">
            <IconButton size="small" onClick={() => execCommand('insertOrderedList')} sx={{ p: 0.5 }}>
              <FormatListNumbered fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* 对齐 */}
        <Box sx={{ display: 'flex', alignItems: 'center', pr: 1, borderRight: 1, borderColor: 'divider', mr: 1 }}>
          <Tooltip title="左对齐">
            <IconButton size="small" onClick={() => execCommand('justifyLeft')} sx={{ p: 0.5 }}>
              <FormatAlignLeft fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="居中">
            <IconButton size="small" onClick={() => execCommand('justifyCenter')} sx={{ p: 0.5 }}>
              <FormatAlignCenter fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="右对齐">
            <IconButton size="small" onClick={() => execCommand('justifyRight')} sx={{ p: 0.5 }}>
              <FormatAlignRight fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* 插入 */}
        <Box sx={{ display: 'flex', alignItems: 'center', pr: 1, borderRight: 1, borderColor: 'divider', mr: 1 }}>
          <Tooltip title="插入表格">
            <IconButton size="small" onClick={insertTable} sx={{ p: 0.5 }}>
              <TableChart fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="插入数据引用">
            <IconButton size="small" onClick={() => {}} sx={{ p: 0.5 }}>
              <DataObject fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* 撤销/重做 */}
        <Box sx={{ display: 'flex', alignItems: 'center', pr: 1, borderRight: 1, borderColor: 'divider', mr: 1 }}>
          <Tooltip title="撤销 (Ctrl+Z)">
            <IconButton size="small" onClick={() => execCommand('undo')} sx={{ p: 0.5 }}>
              <Undo fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="重做 (Ctrl+Y)">
            <IconButton size="small" onClick={() => execCommand('redo')} sx={{ p: 0.5 }}>
              <Redo fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* 保存按钮 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !content}
            sx={{
              height: 28,
              fontSize: '0.75rem',
              bgcolor: content ? 'primary.main' : 'grey.300',
              color: 'white',
              '&:hover': {
                bgcolor: content ? 'primary.dark' : 'grey.400'
              },
              '&:disabled': {
                bgcolor: 'grey.300',
                color: 'white'
              }
            }}
          >
            {saving ? '保存中...' : '保存'}
          </Button>
          {onGenerate && (
            <Button
              size="small"
              variant="contained"
              startIcon={generating ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <AIIcon />}
              onClick={onGenerate}
              disabled={generating}
              sx={{
                height: 28,
                fontSize: '0.75rem',
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark'
                },
                '&:disabled': {
                  bgcolor: 'grey.300'
                }
              }}
            >
              {generating ? '生成中...' : 'AI生成'}
            </Button>
          )}
        </Box>
      </Box>

      {/* 编辑区域 - A4纸张效果 */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          bgcolor: '#e0e0e0',
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            width: '210mm',
            minHeight: '297mm',
            bgcolor: 'white',
            boxShadow: '0 0 15px rgba(0,0,0,0.2), 0 0 3px rgba(0,0,0,0.1)',
            mx: 'auto',
            my: 2,
            // 自动扩展高度以容纳更多内容
            height: 'fit-content',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #ccc',
          }}
        >
          <Box
            ref={editorRef}
            contentEditable
            onInput={handleInput}
            sx={{
              minHeight: '260mm',
              flex: 1,
              p: '20mm 15mm',
              fontSize: '12pt',
              lineHeight: 1.6,
              fontFamily: '"SimSun", "宋体", serif',
              outline: 'none',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              '&:empty:before': {
                content: '"在此编辑章节内容..."',
                color: '#bbb',
              },
              '& h1': {
                fontSize: '16pt',
                fontWeight: 'bold',
                mt: 1.5,
                mb: 0.75,
                color: 'text.primary',
                fontFamily: '"SimHei", "黑体", sans-serif',
              },
              '& h2': {
                fontSize: '14pt',
                fontWeight: 'bold',
                mt: 1.25,
                mb: 0.5,
                color: 'text.primary',
                fontFamily: '"SimHei", "黑体", sans-serif',
              },
              '& h3': {
                fontSize: '12pt',
                fontWeight: 'bold',
                mt: 1,
                mb: 0.5,
                color: 'text.primary',
                fontFamily: '"SimHei", "黑体", sans-serif',
              },
              '& p': {
                my: 0.5,
                textIndent: '2em',
                textAlign: 'justify',
                wordBreak: 'break-all',
              },
              '& ul, & ol': {
                my: 0.5,
                pl: 2,
              },
              '& table': {
                borderCollapse: 'collapse',
                width: '100%',
                my: 1,
                fontSize: '10.5pt',
                '& th, & td': {
                  border: '1px solid #000',
                  p: 0.75,
                },
                '& th': {
                  bgcolor: '#f0f0f0',
                  fontWeight: 'bold',
                },
              },
            }}
          />
        </Box>
      </Box>

      {/* 字数统计 - 固定在底部 */}
      <Box
        sx={{
          textAlign: 'right',
          py: 1,
          px: 2,
          fontSize: '0.75rem',
          color: 'text.disabled',
          bgcolor: '#fafafa',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        字数：{wordCount} 字
        {content && ' · 有未保存的更改'}
      </Box>
    </Box>
  );
};

export default ReportEditor;
